//Imports
import React, {
    useEffect,
    useRef,
    useState,
} from "react";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { splitTextIntoLines } from 'Core/data/Helpers';
import { HasPermission } from "Core/security"

//Controls
import {
    IconButton,
    MenuComponent,
    SearchInput,
    TableButtons,
    TableView,
    useTablePagination
} from "@gpstrackit/gtc-react-controls";

//Components
import { NotificationManager } from "../../../../components/Notifications";
import { Grid } from "@mui/material";

//Actions
import { sendGenericExport } from "Redux/actions";
import { manageSearchWorkOrderTypes, reset } from './redux/slice';
import { openModalCreateEditWorkOrderType, openModalDeleteWorkOrderType, clearRedux } from 'Redux/actions';

//Views
import CreateEditManageWorkOrderType from './modals/createEditManageWorkOrderType'
import DeleteManageWorkOrderType from './modals/deleteManageWorkOrderType'
const WorkOrderType = (props) => {

    //Table refs
    const workOrderTypesTableRef = useRef();

    // Hooks
    const dispatch = useDispatch();

    //Language
    const { messages, locale } = useIntl();

    // Permissions
    const user = useSelector((state) => state.securityRedux.user);
    const editablePermissions = HasPermission(user, "", [350])

    const paginationWorkOrderTypes = useTablePagination({
        dispatch,
        action: manageSearchWorkOrderTypes,
        searchFields: ["name", "dynamicForm_name", "description"],
        tableRef: workOrderTypesTableRef,
        sendGenericExport,
        exportName: "work-order-types",
    });

    //Local States
    const [selectedItems, setSelectedItems] = useState([]);

    //Redux States
    const { loading, items, total } = useSelector((state) => state.manageWorkOrderTypeTabRedux);
    const openModalCreateEdit = useSelector(state => state.manageCreateEditWorkOrderTypeRedux.open);
    const openModalDelete = useSelector((state) => state.manageDeleteManageWorkOrderTypeRedux.open);
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);

    useEffect(() => {
        paginationWorkOrderTypes.setColumns([
            {
                key: "name",
                title: messages['manage_workOrderType_name'],
                functionString: 'return row?.name',
                show: true,
                export: true,
                sort: true,
                width: 200,
                exportValue: (row) => row?.name,
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item xs={10} style={{ wordWrap: 'break-word', width: 200 }}>
                            <p>{row.name}</p>
                        </Grid>
                        {editablePermissions &&
                            <Grid item xs={2}>
                                <MenuComponent
                                    placement="right-start"
                                    options={[messages['edit'], messages['delete']]}
                                    icon="more_vert"
                                    className="showInHover"
                                    onClick={(value) => {
                                        if (value === messages['edit']) {
                                            dispatch(openModalCreateEditWorkOrderType(row?.id));
                                        } else if (value === messages['delete']) {
                                            dispatch(openModalDeleteWorkOrderType({ workOrderType: row }))
                                        }
                                    }}
                                    value={row}
                                />
                            </Grid>
                        }
                    </Grid>
                ),
            },
            {
                key: "dynamicForm_name",
                title: messages['manage_workOrderType_form'],
                functionString: 'return row?.dynamicForm?.name || ""',
                show: true,
                export: true,
                sort: true,
                width: 200,
                exportValue: (row) => row?.dynamicForm?.name,
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item>
                            <p>{row?.dynamicForm?.name}</p>
                        </Grid>
                    </Grid>
                ),
            },
            {
                key: "estimatedDurationMins",
                title: messages['manage_workOrderType_estimatedDuration'],
                functionString: 'return row?.estimatedDurationMins ? row?.estimatedDurationMins + " mins" : ""',
                show: true,
                export: true,
                sort: true,
                width: 150,
                exportValue: (row) => row?.estimatedDurationMins ? `${row?.estimatedDurationMins} mins` : '',
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item>
                            <p>{row?.estimatedDurationMins ? `${row?.estimatedDurationMins} mins` : ''}</p>
                        </Grid>
                    </Grid>
                ),
            },
            {
                key: "description",
                title: messages['manage_workOrderType_description'],
                functionString: `
                    function splitTextIntoLines(text, maxLength) {
                        let result = "";
                        for (let i = 0; i < text.length; i += maxLength) {
                            result += text.slice(i, i + maxLength) + "\\n";
                        }
                        return result.trim();
                    };
                    return row?.description ? splitTextIntoLines(row?.description, 50) : "";
                `,
                show: true,
                export: true,
                sort: true,
                exportValue: (row) => row?.description ? splitTextIntoLines(row?.description, 50) : '',
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item style={{ wordWrap: 'break-word', width: 700 }}>
                            {row?.description}
                        </Grid>
                    </Grid>
                ),
            }
        ]);
        paginationWorkOrderTypes.onChangeSort({ key: "name", order: "asc" });
        return () => dispatch(reset());
    }, [locale]);

    useEffect(() => {
        if (!exportResult || exportName !== "work-order-types") return;

        NotificationManager.info(
            messages["exportInProcessBody"],
            messages["exportInProcessTitle"],
            4000,
            null,
            null,
            "info",
            null
        );
        dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
    }, [exportResult, exportName]);

    const refreshTable = () => {
        setSelectedItems([]);
        workOrderTypesTableRef?.current?.setPage(1);
        workOrderTypesTableRef?.current?.setSelectedItems([]);
        paginationWorkOrderTypes.onChangePage(1);
    };

    return (
        <div>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: "flex" }}>
                            {editablePermissions &&
                                <>
                                    <IconButton
                                        tooltip={messages["add"]}
                                        placement="bottom"
                                        variant="secondary"
                                        icon="add_box"
                                        size="small"
                                        disabled={loading}
                                        onClick={() => {
                                            dispatch(openModalCreateEditWorkOrderType());
                                        }}
                                    />
                                    {
                                        selectedItems?.length ?
                                            <IconButton
                                                tooltip={messages["delete"]}
                                                placement="bottom"
                                                variant="secondary"
                                                icon="delete"
                                                size="small"
                                                onClick={() => {
                                                    dispatch(openModalDeleteWorkOrderType({ workOrderTypeList: selectedItems }))
                                                }}
                                                style={{ opacity: !selectedItems.length && "0.6" }}
                                                isDisabled={!selectedItems.length}
                                            /> : <></>
                                    }
                                </>
                            }
                            <TableButtons
                                messages={messages}
                                {...paginationWorkOrderTypes}
                                buttons={["export", "orderColumn"]}
                                onExport={(type) => {
                                    paginationWorkOrderTypes.onExport(type);
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages["manage_workOrderType_search_by_name_form_description"]}
                        searchText={paginationWorkOrderTypes.searchText}
                        onSearchText={paginationWorkOrderTypes.onSearchChange}
                    />
                </Grid>
            </Grid>
            <TableView
                keyColumn="id"
                loading={loading}
                topheight={235}
                data={items}
                total={total}
                exportOrientation="landscape"
                onSelectedItems={(items) => setSelectedItems(items)}
                {...paginationWorkOrderTypes}
                hideDragable
                hideAllInPagination
            />
            {openModalCreateEdit && <CreateEditManageWorkOrderType refresh={refreshTable} />}
            {openModalDelete && <DeleteManageWorkOrderType refresh={refreshTable} />}
        </div>
    )

}

export default WorkOrderType;